import React, {useRef, useState} from 'react';
import useCustomForm from "../../../services/useCustomForm";
import {CCol, CFormGroup, CInput, CLabel, CRow, CSelect, CSwitch} from "@coreui/react";
import AsyncSelect from "react-select/async";
import CIcon from "@coreui/icons-react";
import Form, {FormApi} from "../../../components/form";
import {getApi} from "../../../apis/backendApis";
import useAlert from "../../../services/useAlert";
import ConfirmModal from "../../../components/confirmModal";
import {
    LIMITE_SIMULTANEO_MAXIMO,
    LIMITE_SIMULTANEO_PADRAO,
    LIMITE_SIMULTANEO_RECOMENDADO,
    StackExecuteTask,
    StackExecuteTaskAIList,
    StackExecuteTaskList,
    TypeExecuteTask,
    TypeExecuteTaskList
} from "./index";
import TestCasePicker from "./testCasePicker";
import SelectedItemsList, {ItemSelecionado} from "./selectedItemsList";
import '../../../scss/styles-tester.scss';

const moverItem = <T, >(lista: Array<T>, origem: number, destino: number): Array<T> => {
    const copia = [...lista];
    const [item] = copia.splice(origem, 1);
    copia.splice(destino, 0, item);
    return copia;
}

const ExecuteTaskForm = () => {
    const [showAlert] = useAlert();
    const [, setFormApi] = useState<FormApi>();

    const initialValues: any = {
        id: '',
        name: '',
        type: TypeExecuteTask.TYPE_TEST_USER,
        testCase: [],
        mockup: [],
        issueId: '',
        gerarRelatorio: false,
        stack: StackExecuteTask.CYPRESS,
        executarSimultaneo: false,
        limiteSimultaneo: LIMITE_SIMULTANEO_PADRAO
    }

    // Limite acima do recomendado: pede confirmação uma vez (o valor que já veio salvo não pergunta de novo)
    const [limitePendente, setLimitePendente] = useState<{valor: number, depois?: () => void} | null>(null);
    // Refs: o salvar que espera a confirmação roda com a função da hora em que o modal abriu
    const limiteConfirmadoRef = useRef<number | null>(null);
    const limiteSalvoRef = useRef<number | null>(null);

    const {
        values,
        handleChange,
        handleSubmit,
        handleSubmitValues,
        setValues
    } = useCustomForm({
        initialValues,
        route: 'execute-task',
        onSubmit: formulario => submitValues(formulario.values),
        onLoadDadosCompleted: (dados) => {
            // O plano guarda os casos e mockups como objeto indexado: aqui viram lista na ordem salva
            setValues({
                ...dados,
                testCase: Object.values(dados.testCase || {}),
                mockup: Object.values(dados.mockup || {}),
                executarSimultaneo: dados.executarSimultaneo === true,
                limiteSimultaneo: Number(dados.limiteSimultaneo) || LIMITE_SIMULTANEO_PADRAO,
            });
            limiteSalvoRef.current = Number(dados.limiteSimultaneo) || null;
        }
    });

    const isAI = Number(values.type) === TypeExecuteTask.TYPE_AI_TEST;
    const isCypress = Number(values.stack) === StackExecuteTask.CYPRESS;
    const simultaneo = isCypress && values.executarSimultaneo === true;
    const limite = Math.min(LIMITE_SIMULTANEO_MAXIMO, Math.max(1, Number(values.limiteSimultaneo) || LIMITE_SIMULTANEO_PADRAO));
    const precisaConfirmarLimite = (valor: number) => simultaneo && valor > LIMITE_SIMULTANEO_RECOMENDADO
        && valor !== limiteConfirmadoRef.current && valor !== limiteSalvoRef.current;

    const aoSairDoLimite = () => {
        setValues({...values, limiteSimultaneo: limite});
        if (precisaConfirmarLimite(limite)) {
            setLimitePendente({valor: limite});
        }
    }
    const casos: Array<ItemSelecionado> = values.testCase || [];
    const mockups: Array<ItemSelecionado> = values.mockup || [];

    // Os padrões de stack/relatório só mudam quando o usuário troca o campo, não ao carregar um plano salvo
    const aoMudarTipo = (evento: any) => {
        const tipo = Number(evento.target.value);
        if (tipo === TypeExecuteTask.TYPE_AI_TEST) {
            setValues({...values, type: tipo, stack: StackExecuteTask.AGENT_DEVELOPERS, gerarRelatorio: false});
        } else {
            const stack = Number(values.stack) === StackExecuteTask.AGENT_DEVELOPERS ? StackExecuteTask.CYPRESS : values.stack;
            setValues({...values, type: tipo, stack, gerarRelatorio: tipo === TypeExecuteTask.TYPE_JOB ? true : values.gerarRelatorio});
        }
    }

    const aoMudarStack = (evento: any) => {
        const stack = Number(evento.target.value);
        setValues({...values, stack, gerarRelatorio: stack === StackExecuteTask.CYPRESS});
    }

    const submitValues = (formulario: any) => {
        if (!isAI && (casos.length === 0 || mockups.length === 0)) {
            showAlert({
                msg: 'Inclua pelo menos um caso de teste e um mockup: sem eles o plano não tem o que executar.',
                type: 'warning',
                toaster: true,
            });
            return;
        }
        if (precisaConfirmarLimite(limite)) {
            setLimitePendente({valor: limite, depois: () => submitValues(formulario)});
            return;
        }
        handleSubmitValues({
            ...formulario,
            gerarRelatorio: !!formulario.gerarRelatorio,
            executarSimultaneo: simultaneo,
            limiteSimultaneo: limite,
            testCase: casos.map(({id, name, codigo}) => ({id, name, codigo})),
            mockup: mockups.map(({id, name}) => ({id, name})),
        });
    }

    // ------------------------------------------------------------------ casos e mockups

    const adicionarCasos = (novos: Array<ItemSelecionado>) => {
        const existentes = new Set(casos.map(caso => String(caso.id)));
        const unicos = novos.filter(caso => !existentes.has(String(caso.id)));
        if (unicos.length) {
            setValues({...values, testCase: [...casos, ...unicos]});
        }
    }

    const removerCaso = (id: number | string) =>
        setValues({...values, testCase: casos.filter(caso => String(caso.id) !== String(id))});

    const adicionarMockups = (novos: Array<ItemSelecionado>) => {
        const existentes = new Set(mockups.map(mockup => String(mockup.id)));
        const unicos = novos.filter(mockup => !existentes.has(String(mockup.id)));
        if (unicos.length) {
            setValues({...values, mockup: [...mockups, ...unicos]});
        }
    }

    const removerMockup = (id: number | string) =>
        setValues({...values, mockup: mockups.filter(mockup => String(mockup.id) !== String(id))});

    const carregarMockups = (termo: string): Promise<any> =>
        getApi().get('/mockup/suggest/' + encodeURIComponent(termo || ' ')).then(response => response.data);

    const idsMockups = new Set(mockups.map(mockup => String(mockup.id)));

    const execucoes = casos.length * mockups.length;

    return (<Form entity={'Plano de Execução ' + values.id} handleSubmit={handleSubmit} onFormReady={setFormApi} className="tester-form-plano">
        <CRow>
            <CCol lg={isAI ? 12 : 6} className="mb-3">
                <div className="tester-secao">
                    <div className="tester-secao-titulo">Dados do plano</div>
                    <CFormGroup>
                        <CLabel className={'text-danger'} htmlFor="name">Nome</CLabel>
                        <CInput type="text" id="name" name="name" maxLength={200} onChange={handleChange} value={values.name}
                                placeholder="Ex.: Regressão sprint 3.77" required/>
                    </CFormGroup>
                    <CRow>
                        <CCol sm={6}>
                            <CFormGroup>
                                <CLabel className={'text-danger'} htmlFor="type">Tipo</CLabel>
                                <CSelect id="type" name="type" onChange={aoMudarTipo} value={values.type} required>
                                    {TypeExecuteTaskList.getList().map(item => (
                                        <option key={item.value} value={item.value}>{item.description}</option>
                                    ))}
                                </CSelect>
                            </CFormGroup>
                        </CCol>
                        <CCol sm={6}>
                            <CFormGroup>
                                <CLabel className={'text-danger'} htmlFor="stack">Stack para teste</CLabel>
                                <CSelect id="stack" name="stack" onChange={aoMudarStack} value={values.stack} required>
                                    {(isAI ? StackExecuteTaskAIList : StackExecuteTaskList).getList().map(item => (
                                        <option key={item.value} value={item.value}>{item.description}</option>
                                    ))}
                                </CSelect>
                            </CFormGroup>
                        </CCol>
                    </CRow>
                    {isAI && (
                        <CFormGroup>
                            <CLabel htmlFor="issueId">Issue ID <small className="text-muted">(opcional)</small></CLabel>
                            <CInput type="number" id="issueId" name="issueId" min={1} onChange={handleChange}
                                    value={values.issueId} placeholder="Número da issue (opcional)"/>
                        </CFormGroup>
                    )}
                </div>
            </CCol>

            {!isAI && (
                <CCol lg={6} className="mb-3">
                    <div className="tester-secao">
                        <div className="tester-secao-titulo">Execução</div>
                        {isCypress && (
                            <div className="mb-3">
                                <label className="tester-switch">
                                    <CSwitch color="primary" shape="pill" id="executarSimultaneo" name="executarSimultaneo"
                                             checked={simultaneo} onChange={handleChange}/>
                                    Executar vários casos ao mesmo tempo
                                </label>
                                {simultaneo && (
                                    <div className="d-flex align-items-center mt-2" style={{gap: '.5rem'}}>
                                        <CLabel htmlFor="limiteSimultaneo" className="mb-0">Quantos ao mesmo tempo</CLabel>
                                        <CInput type="number" id="limiteSimultaneo" name="limiteSimultaneo" style={{maxWidth: '6rem'}}
                                                min={1} max={LIMITE_SIMULTANEO_MAXIMO} step={1}
                                                value={values.limiteSimultaneo}
                                                onChange={(evento: any) => setValues({...values, limiteSimultaneo: evento.target.value})}
                                                onBlur={aoSairDoLimite}/>
                                    </div>
                                )}
                                <div className="tester-ajuda mt-1">
                                    {simultaneo
                                        ? 'Roda até ' + limite + ' casos ao mesmo tempo, cada um no seu navegador. Os outros esperam na fila do admin e entram conforme os primeiros terminam. Não recomendamos mais de ' + LIMITE_SIMULTANEO_RECOMENDADO + ': a máquina pode ficar sem memória e derrubar casos.'
                                        : 'Desligado, os casos rodam um por vez, na ordem da lista.'}
                                </div>
                            </div>
                        )}

                        {isCypress && (
                            <label className="tester-switch mb-3">
                                <CSwitch color="primary" shape="pill" id="gerarRelatorio" name="gerarRelatorio"
                                         checked={!!values.gerarRelatorio} onChange={handleChange}
                                         disabled={Number(values.type) === TypeExecuteTask.TYPE_JOB}/>
                                Gerar relatório e vídeo de cada caso
                            </label>
                        )}

                        <div className="tester-resumo-plano">
                            {casos.length} {casos.length === 1 ? 'caso' : 'casos'} × {mockups.length} {mockups.length === 1 ? 'mockup' : 'mockups'} ={' '}
                            <strong>{execucoes} {execucoes === 1 ? 'execução' : 'execuções'}</strong>
                            {execucoes > 1 && (simultaneo ? ', até ' + Math.min(limite, execucoes) + ' ao mesmo tempo' : ', uma por vez')}
                        </div>
                    </div>
                </CCol>
            )}
        </CRow>

        {!isAI && (
            <CRow className="tester-plano-listas">
                <CCol lg={6} className="mb-3">
                    <div className="tester-secao">
                        <div className="tester-secao-titulo">
                            <span>Casos de teste <span className="badge badge-secondary ml-1">{casos.length}</span></span>
                            <span className="tester-ajuda font-weight-normal">Arraste para definir a ordem da execução</span>
                        </div>
                        <TestCasePicker selecionados={casos} onAdicionar={adicionarCasos} onRemover={removerCaso}/>
                        <SelectedItemsList
                            droppableId="casos"
                            itens={casos}
                            vazio="Nenhum caso no plano. Busque por código, nome ou grupo, ou use Adicionar todos."
                            onRemover={removerCaso}
                            onRemoverTodos={() => setValues({...values, testCase: []})}
                            onReordenar={(origem, destino) => setValues({...values, testCase: moverItem(casos, origem, destino)})}
                        />
                    </div>
                </CCol>
                <CCol lg={6} className="mb-3">
                    <div className="tester-secao">
                        <div className="tester-secao-titulo">
                            <span>Mockups de dados <span className="badge badge-secondary ml-1">{mockups.length}</span></span>
                        </div>
                        <div className="mb-2">
                            <AsyncSelect
                                isMulti
                                defaultOptions
                                loadOptions={carregarMockups}
                                value={mockups.map(mockup => ({value: mockup.id, label: mockup.name}))}
                                onChange={(_: any, meta: any) => {
                                    if (meta.action === 'select-option' && meta.option) {
                                        adicionarMockups([{id: meta.option.value, name: meta.option.label}]);
                                    } else if (meta.action === 'deselect-option' && meta.option) {
                                        removerMockup(meta.option.value);
                                    }
                                }}
                                formatOptionLabel={(opcao: any) => (
                                    <span className={'tester-opcao' + (idsMockups.has(String(opcao.value)) ? ' incluido' : '')}>
                                        <span className="check">{idsMockups.has(String(opcao.value)) && <CIcon name="cil-check"/>}</span>
                                        <span className="nome">{opcao.label}</span>
                                    </span>
                                )}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                controlShouldRenderValue={false}
                                backspaceRemovesValue={false}
                                isClearable={false}
                                placeholder="Buscar mockup pelo nome"
                                noOptionsMessage={() => 'Nenhum mockup encontrado'}
                                loadingMessage={() => 'Buscando...'}
                                aria-label="Adicionar mockups ao plano"
                                // No body: a seção de mockups tem rolagem própria e cortaria o menu
                                menuPortalTarget={document.body}
                                styles={{menuPortal: (base: any) => ({...base, zIndex: 1060})}}
                            />
                        </div>
                        <SelectedItemsList
                            droppableId="mockups"
                            itens={mockups}
                            vazio="Nenhum mockup no plano. Cada caso roda uma vez para cada mockup."
                            onRemover={removerMockup}
                            onRemoverTodos={() => setValues({...values, mockup: []})}
                            onReordenar={(origem, destino) => setValues({...values, mockup: moverItem(mockups, origem, destino)})}
                        />
                    </div>
                </CCol>
            </CRow>
        )}

        <ConfirmModal
            aberto={limitePendente !== null}
            titulo="Muitos casos ao mesmo tempo"
            rotuloConfirmar={'Rodar ' + (limitePendente?.valor ?? limite) + ' ao mesmo tempo'}
            corConfirmar="warning"
            onConfirmar={() => {
                const pendente = limitePendente;
                limiteConfirmadoRef.current = pendente ? pendente.valor : null;
                setLimitePendente(null);
                pendente?.depois?.();
            }}
            onFechar={() => {
                setValues({...values, limiteSimultaneo: LIMITE_SIMULTANEO_RECOMENDADO});
                setLimitePendente(null);
            }}
        >
            <p className="mb-2">
                Não recomendamos rodar mais de {LIMITE_SIMULTANEO_RECOMENDADO} casos ao mesmo tempo. Cada caso abre um navegador e grava
                vídeo: com muitos juntos a máquina pode ficar sem memória e derrubar casos no meio.
            </p>
            <p className="mb-0">Use "Voltar" para ficar com {LIMITE_SIMULTANEO_RECOMENDADO}.</p>
        </ConfirmModal>
    </Form>)
}
export default ExecuteTaskForm;
